import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ghs, num, formatDate, todayISODate, monthKey } from "../lib/format";
import { Plus, Trash, X, UploadSimple } from "@phosphor-icons/react";
import ExportButton from "../components/ExportButton";

const DEFAULT_FORM = {
  date: todayISODate(),
  job_description: "",      // Client
  location: "",              // Destination
  requested_by: "SETH",
  vehicle_number: "",
  trip_number: 1,
  cargo: "",
  quantity: "",              // Yard litres
  unit_price: "",            // Yard price/L
  topup_litres: "",
  topup_price: "",
  total_price: "",
  driver_name: "",
  issued_by: "COLLINS",
  unit_count: "",            // For station-type assets only: e.g. 12 washes
};

export default function Dispenses() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState({ month: "", driver: "", vehicle: "", client: "" });
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [uploadMsg, setUploadMsg] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleTripLogUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setUploadMsg("");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/import/trip-log", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadMsg(`✓ Imported ${data.trip_rows} trips · ${data.matched_updates} matched, ${data.unmatched_inserted} new entries`);
      await load();
    } catch (err) {
      setUploadMsg(`✗ ${formatApiErrorDetail(err.response?.data?.detail) || err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTimeout(() => setUploadMsg(""), 8000);
    }
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (filter.month) params.month = filter.month;
    if (filter.driver) params.driver = filter.driver;
    if (filter.vehicle) params.vehicle = filter.vehicle;
    if (filter.client) params.job = filter.client;
    const [d, t, dr] = await Promise.all([
      api.get("/dispenses", { params }),
      api.get("/trucks"),
      api.get("/drivers"),
    ]);
    setRows(d.data);
    setTrucks(t.data);
    setDrivers(dr.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.month, filter.driver, filter.vehicle, filter.client]);

  // If the operator picks a station-type asset (washing station, Kalmar handler…), we surface
  // an extra "unit_count" field with the station's own unit label so we can build L/wash ratios.
  const selectedAsset = trucks.find((t) => (t.vehicle_number || "").toUpperCase() === (form.vehicle_number || "").toUpperCase());
  const isStation = selectedAsset?.asset_type === "station";
  const unitLabel = selectedAsset?.unit_label || "ops";

  const computedTotal = () => {
    const q = parseFloat(form.quantity) || 0;
    const u = parseFloat(form.unit_price) || 0;
    const tl = parseFloat(form.topup_litres) || 0;
    const tp = parseFloat(form.topup_price) || 0;
    return q * u + tl * tp;
  };

  const onChange = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: v };
      // Auto-compute total whenever any pricing field changes (unless user has manually edited total)
      const auto = (() => {
        const q = parseFloat(next.quantity) || 0;
        const u = parseFloat(next.unit_price) || 0;
        const tl = parseFloat(next.topup_litres) || 0;
        const tp = parseFloat(next.topup_price) || 0;
        return q * u + tl * tp;
      })();
      if (["quantity", "unit_price", "topup_litres", "topup_price"].includes(k)) {
        next.total_price = auto > 0 ? auto.toFixed(2) : "";
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = {
        date: new Date(form.date).toISOString(),
        job_description: form.job_description,
        location: form.location,
        requested_by: form.requested_by,
        vehicle_number: form.vehicle_number,
        trip_number: parseInt(form.trip_number) || 1,
        cargo: form.cargo,
        quantity: parseFloat(form.quantity),
        unit_price: parseFloat(form.unit_price),
        topup_litres: form.topup_litres ? parseFloat(form.topup_litres) : 0,
        topup_price: form.topup_price ? parseFloat(form.topup_price) : 0,
        total_price: form.total_price ? parseFloat(form.total_price) : null,
        driver_name: form.driver_name,
        issued_by: form.issued_by,
        unit_count: form.unit_count ? parseFloat(form.unit_count) : null,
      };
      await api.post("/dispenses", payload);
      setOpen(false);
      setForm(DEFAULT_FORM);
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this dispense?")) return;
    await api.delete(`/dispenses/${id}`);
    await load();
  };

  const totalYard = rows.reduce((s, r) => s + (r.quantity || 0), 0);
  const totalTopup = rows.reduce((s, r) => s + (r.topup_litres || 0), 0);
  const totalRevenue = rows.reduce((s, r) => s + (r.total_price || 0), 0);
  const months = Array.from(new Set(rows.map((r) => monthKey(r.date)))).sort().reverse();
  const clients = Array.from(new Set(rows.map((r) => r.job_description).filter(Boolean))).sort();

  return (
    <div data-testid="dispenses-page">
      <PageHeader
        subtitle="Fuel station log"
        title="Dispenses"
        action={
          <div className="flex items-center gap-3">
            <ExportButton
              endpoint="reports/dispenses/export"
              filenameBase={`dispenses-${filter.month || new Date().toISOString().slice(0, 7)}`}
              params={{ month: filter.month || undefined }}
              testidPrefix="dispenses-export"
            />
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleTripLogUpload} className="hidden" data-testid="trip-log-input" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="trip-log-upload-button"
              className="border border-slate-300 bg-white text-ps-deep px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-slate-50 transition-colors disabled:opacity-50"
              title="Upload a Trucking Report Excel — merges trip# and cargo onto existing dispenses"
            >
              <UploadSimple size={16} weight="bold" />
              {uploading ? "Importing…" : "Import trip log"}
            </button>
            <button
              onClick={() => setOpen(true)}
              data-testid="new-dispense-button"
              className="bg-ps-blue text-white px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-ps-deep transition-colors shadow-sm"
            >
              <Plus size={16} weight="bold" /> New dispense
            </button>
          </div>
        }
      />

      {uploadMsg && (
        <div className={`px-8 py-2 text-sm border-b ${uploadMsg.startsWith("✓") ? "bg-lime-50 text-lime-800 border-lime-200" : "bg-red-50 text-ps-red border-red-200"}`} data-testid="upload-message">
          {uploadMsg}
        </div>
      )}

      {/* Filters & summary */}
      <div className="px-8 py-4 bg-slate-100 border-b border-slate-200 flex flex-wrap gap-6 items-end" data-testid="dispenses-filters">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Month</label>
          <select value={filter.month} onChange={(e) => setFilter({ ...filter, month: e.target.value })} className="border border-slate-300 rounded-md bg-white px-2 py-2 text-sm font-mono-data" data-testid="filter-month">
            <option value="">All</option>
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Client</label>
          <select value={filter.client} onChange={(e) => setFilter({ ...filter, client: e.target.value })} className="border border-slate-300 rounded-md bg-white px-2 py-2 text-sm font-mono-data" data-testid="filter-client">
            <option value="">All</option>
            {clients.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Driver</label>
          <select value={filter.driver} onChange={(e) => setFilter({ ...filter, driver: e.target.value })} className="border border-slate-300 rounded-md bg-white px-2 py-2 text-sm font-mono-data" data-testid="filter-driver">
            <option value="">All</option>
            {drivers.map((d) => <option key={d.id} value={d.name}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Truck</label>
          <select value={filter.vehicle} onChange={(e) => setFilter({ ...filter, vehicle: e.target.value })} className="border border-slate-300 rounded-md bg-white px-2 py-2 text-sm font-mono-data" data-testid="filter-vehicle">
            <option value="">All</option>
            {trucks.map((t) => <option key={t.id} value={t.vehicle_number}>{t.vehicle_number}</option>)}
          </select>
        </div>
        {(filter.month || filter.driver || filter.vehicle || filter.client) && (
          <button onClick={() => setFilter({ month: "", driver: "", vehicle: "", client: "" })} className="text-xs uppercase tracking-wider text-slate-600 hover:text-ps-deep flex items-center gap-1" data-testid="filter-clear">
            <X size={14} /> Clear filters
          </button>
        )}
        <div className="ml-auto flex gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Records</div>
            <div className="font-mono-data tabular text-xl text-ps-deep">{num(rows.length)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Yard litres</div>
            <div className="font-mono-data tabular text-xl text-ps-blue">{num(totalYard)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Top-up litres</div>
            <div className="font-mono-data tabular text-xl text-ps-orange">{num(totalTopup)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Revenue</div>
            <div className="font-mono-data tabular text-xl text-lime-700">{ghs(totalRevenue)}</div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left">Date</th>
                <th className="px-3 py-3 text-left">Trip</th>
                <th className="px-3 py-3 text-left">Client</th>
                <th className="px-3 py-3 text-left">Cargo</th>
                <th className="px-3 py-3 text-left">Truck</th>
                <th className="px-3 py-3 text-left">Driver</th>
                <th className="px-3 py-3 text-right">Yard&nbsp;L</th>
                <th className="px-3 py-3 text-right">Yard&nbsp;@</th>
                <th className="px-3 py-3 text-right">Top-up&nbsp;L</th>
                <th className="px-3 py-3 text-right">Total&nbsp;L</th>
                <th className="px-3 py-3 text-right">Total&nbsp;(GHS)</th>
                <th className="px-3 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="dispenses-table-body">
              {loading && (<tr><td colSpan={12} className="p-6"><div className="brand-progress w-1/3" /></td></tr>)}
              {!loading && rows.length === 0 && (<tr><td colSpan={12} className="p-8 text-center text-slate-500">No dispenses match filters.</td></tr>)}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono-data text-xs whitespace-nowrap">{formatDate(r.date)}</td>
                  <td className="px-3 py-2 font-mono-data text-xs text-slate-500">
                    {r.trip_number ? <span className="px-1.5 py-0.5 bg-slate-100 rounded">#{r.trip_number}</span> : "—"}
                  </td>
                  <td className="px-3 py-2 font-semibold text-ps-deep">{r.activity || r.job_description || "—"}</td>
                  <td className="px-3 py-2 text-slate-600 text-xs max-w-[140px] truncate" title={r.cargo}>{r.cargo || "—"}</td>
                  <td className="px-3 py-2 font-mono-data text-ps-blue">{r.vehicle_number}</td>
                  <td className="px-3 py-2">{r.driver_name}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular text-ps-blue">{num(r.quantity)}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular text-slate-500 text-xs">{r.unit_price ? r.unit_price.toFixed(2) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular text-ps-orange">{r.topup_litres ? num(r.topup_litres) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular font-semibold">{num(r.total_litres ?? r.quantity)}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular font-semibold text-lime-700">{r.total_price ? ghs(r.total_price) : "—"}</td>
                  <td className="px-3 py-2">
                    <button onClick={() => del(r.id)} data-testid={`delete-dispense-${r.id}`} className="text-slate-400 hover:text-ps-red"><Trash size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slide-over form */}
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-end" onClick={() => setOpen(false)} data-testid="dispense-dialog">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white h-full overflow-y-auto border-l border-slate-200">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">New record</div>
                <h2 className="font-display font-extrabold text-2xl tracking-tight text-ps-deep">Dispense fuel</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-500 hover:text-ps-deep" data-testid="dispense-dialog-close"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <FormField label="Date *" type="date" value={form.date} onChange={onChange("date")} testid="dispense-date" required />
              <FormField label="Client *" value={form.job_description} onChange={onChange("job_description")} testid="dispense-job" placeholder="HAPAG / STOLT / DE-HUES …" required />
              <FormField label="Destination" value={form.location} onChange={onChange("location")} testid="dispense-location" placeholder="GPHA / KUMASI / LOMÉ …" />
              <FormField label="Requested by" value={form.requested_by} onChange={onChange("requested_by")} testid="dispense-requested-by" />
              <FormField label="Truck (vehicle number) *" value={form.vehicle_number} onChange={onChange("vehicle_number")} testid="dispense-vehicle" required placeholder="GR 7308" />
              <FormField label="Driver name *" value={form.driver_name} onChange={onChange("driver_name")} testid="dispense-driver" required />
              <div className="grid grid-cols-3 gap-3">
                <FormField label="Trip #" type="number" min="1" step="1" value={form.trip_number} onChange={onChange("trip_number")} testid="dispense-trip-number" />
                <div className="col-span-2">
                  <FormField label="Cargo / container" value={form.cargo} onChange={onChange("cargo")} testid="dispense-cargo" placeholder="STEARIN / 700 bags poultry feed …" />
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold mb-3">Fuel from yard tank</div>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Yard litres *" type="number" step="0.01" value={form.quantity} onChange={onChange("quantity")} testid="dispense-qty" required />
                  <FormField label="Yard price /L (GHS) *" type="number" step="0.01" value={form.unit_price} onChange={onChange("unit_price")} testid="dispense-unit" required />
                </div>
              </div>

              {isStation && (
                <div className="border-t border-slate-200 pt-4 bg-lime-50 -mx-6 px-6 py-4" data-testid="station-unit-panel">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-lime-700 font-semibold mb-1">Yard equipment · {selectedAsset.vehicle_number}</div>
                  <p className="text-xs text-slate-600 mb-3">Record the number of <strong>{unitLabel}</strong> this fuel covered so we can track L per {singularUnit(unitLabel)}.</p>
                  <FormField
                    label={`${unitLabel} performed`}
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.unit_count}
                    onChange={onChange("unit_count")}
                    testid="dispense-unit-count"
                    placeholder={`e.g. 12 ${unitLabel}`}
                  />
                </div>
              )}

              <div className="border-t border-slate-200 pt-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-ps-orange font-semibold mb-3">Top-up fuel (optional)</div>
                <p className="text-xs text-slate-500 mb-3">Fuel bought elsewhere on the trip — does <strong>not</strong> reduce yard stock.</p>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Top-up litres" type="number" step="0.01" value={form.topup_litres} onChange={onChange("topup_litres")} testid="dispense-topup-l" />
                  <FormField label="Top-up price /L (GHS)" type="number" step="0.01" value={form.topup_price} onChange={onChange("topup_price")} testid="dispense-topup-p" />
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4 bg-slate-50 -mx-6 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Total fuel</div>
                    <div className="font-mono-data tabular text-xl text-ps-deep">{num((parseFloat(form.quantity) || 0) + (parseFloat(form.topup_litres) || 0))} L</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Total cost</div>
                    <div className="font-mono-data tabular text-xl text-lime-700">{ghs(computedTotal())}</div>
                  </div>
                </div>
                <FormField label="Override total (GHS)" type="number" step="0.01" value={form.total_price} onChange={onChange("total_price")} testid="dispense-total" />
              </div>

              <FormField label="Issued by" value={form.issued_by} onChange={onChange("issued_by")} testid="dispense-issued-by" />
              {err ? <div className="border border-red-300 bg-red-50 text-ps-red text-sm px-3 py-2 rounded-md">{err}</div> : null}
            </div>
            <div className="p-6 border-t border-slate-200 flex gap-3 sticky bottom-0 bg-white">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-slate-300 rounded-md py-2.5 text-sm hover:bg-slate-100">Cancel</button>
              <button type="submit" data-testid="dispense-submit" className="flex-1 bg-ps-blue text-white py-2.5 rounded-md text-sm font-semibold hover:bg-ps-deep transition-colors">Save dispense</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function FormField({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      <input
        {...props}
        data-testid={testid}
        className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono-data"
      />
    </div>
  );
}

// Convert a plural unit label ("washes") to its singular form ("wash") for copy like "L per wash".
function singularUnit(label) {
  if (!label) return "unit";
  const lower = String(label).toLowerCase();
  const irregular = { washes: "wash", boxes: "box", batches: "batch", cleans: "clean", flushes: "flush", passes: "pass" };
  if (irregular[lower]) return irregular[lower];
  if (lower.endsWith("ies") && lower.length > 3) return lower.slice(0, -3) + "y";
  if (lower.endsWith("s") && lower.length > 1) return lower.slice(0, -1);
  return lower;
}

